import { useEffect, useState } from "react";

import { bomSlice } from "fond/api";
import { BomOutput } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";

type VersionBomsState = {
  boms: Record<string, BomOutput>;
  isLoading: boolean;
};

/**
 * Fetch the BOM for each of the given version IDs.
 *
 * `useGetVersionBomQuery` can't be called in a loop for a dynamic list of versions (violates the rules of
 * hooks), so instead we dispatch the underlying query action directly for each version and collect the results.
 * RTK Query still dedupes/caches these against any other component querying the same version's BOM.
 */
export function useVersionBoms(versionIds: string[]): VersionBomsState {
  const dispatch = useAppDispatch();
  const [state, setState] = useState<VersionBomsState>({ boms: {}, isLoading: versionIds.length > 0 });

  useEffect(() => {
    if (versionIds.length === 0) {
      setState({ boms: {}, isLoading: false });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, isLoading: true }));

    const subscriptions = versionIds.map((versionId) => dispatch(bomSlice.endpoints.getVersionBom.initiate(versionId)));

    Promise.all(subscriptions).then((results) => {
      if (cancelled) return;

      const boms: Record<string, BomOutput> = {};
      results.forEach((result, index) => {
        if (result.data) boms[versionIds[index]] = result.data;
      });
      setState({ boms, isLoading: false });
    });

    return () => {
      cancelled = true;
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, versionIds.join(",")]);

  return state;
}
